/**
 * NonCombatPersonnelSection.jsx
 * 
 * Displays headquarters and support unit casualties in Tab 5 after calculation.
 * Shows complete casualty breakdown for rear-area personnel with medical planning context.
 * 
 * @version 1.1.0
 * @date 2026-03-26
 * @author PSP/CRE Development Team
 *
 * v1.1.0 (March 26, 2026) — CLEANUP-003: MUI Grid v2 prop migration.
 *   Replaced all <Grid item xs={n}> / <Grid item xs={n} md={m}> with Grid v2 syntax:
 *   <Grid size={n}> and <Grid size={{ xs: n, md: m }}>. No visual change.
 * 
 * DESIGN RATIONALE:
 * - Moved from Tab 3 to Tab 5 for better discoverability
 * - Card-based layout for HQ vs Support comparison
 * - Expandable details for progressive disclosure
 * - Clear terminology: "HQ & Support" (unit types) not "Non-Combat" (casualty types)
 * 
 * Related: BUG-026, PHASE3-UX-001
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Button,
  Collapse,
  Divider
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';

/**
 * Main component for displaying HQ and Support unit casualties
 */
const NonCombatPersonnelSection = ({ 
  hqCasualties, 
  supportCasualties, 
  duration = 10,
  nationalStandard = 'NATO',
  echelon = 'DIVISION'
}) => {
  const { t } = useTranslation();
  // State for expandable sections
  const [hqExpanded, setHqExpanded] = useState(false);
  const [supportExpanded, setSupportExpanded] = useState(false);

  // Validation: Don't show section if no data
  if (!hqCasualties && !supportCasualties) {
    return null;
  }

  // Helper function to calculate percentages safely
  const calcPercent = (part, total) => {
    if (!total || total === 0) return 0;
    return Math.round((part / total) * 100);
  };

  // BUG-T34 fix: largest-remainder method to ensure percentages sum to exactly 100
  // Takes an array of counts; returns array of integer percentages summing to 100
  const largestRemainderPcts = (counts, total) => {
    if (!total || total === 0) return counts.map(() => 0);
    const rawPcts = counts.map(c => (c / total) * 100);
    const floors  = rawPcts.map(Math.floor);
    const sum = floors.reduce((a, b) => a + b, 0);
    const remainder = 100 - sum;
    // Distribute remaining points to items with largest fractional parts
    const indices = rawPcts
      .map((v, i) => ({ i, frac: v - Math.floor(v) }))
      .sort((a, b) => b.frac - a.frac)
      .slice(0, remainder)
      .map(x => x.i);
    indices.forEach(i => floors[i]++);
    return floors;
  };

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString();
  };

  return (
    <Card 
      sx={{ 
        mb: 3, 
        border: '2px solid #ffa726',
        borderRadius: 2
      }}
    >
      <CardContent>
        {/* ============================================ */}
        {/* SECTION HEADER                              */}
        {/* ============================================ */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              HEADQUARTERS &amp; SUPPORT UNIT CASUALTIES
            </Typography>
          </Box>
          
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Rear-Area Personnel
          </Typography>
          
          <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
            <Typography variant="body2">
              Shows casualties from HQ staff (division, brigade, battalion headquarters) and 
              support units (signal, medical, logistics, transportation, etc.). Combat brigade 
              casualties (including their DNBI) are shown in the main calculation summary above.
            </Typography>
          </Alert>
        </Box>

        {/* ============================================ */}
        {/* SUMMARY CARDS - HQ AND SUPPORT              */}
        {/* ============================================ */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          
          {/* HQ PERSONNEL CARD */}
          {hqCasualties && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: 3, 
                  bgcolor: 'primary.50',
                  border: '1px solid',
                  borderColor: 'primary.main',
                  height: '100%'
                }}
              >
                <Typography 
                  variant="h6" 
                  color="primary" 
                  gutterBottom 
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  HEADQUARTERS STAFF
                </Typography>
                
                {/* Personnel and Casualties */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Personnel
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {formatNumber(hqCasualties.total_hq_personnel)}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Casualties
                    </Typography>
                    <Typography variant="h5" color="error" sx={{ fontWeight: 600 }}>
                      {formatNumber(hqCasualties.total)}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Casualty Rate */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Casualty Rate
                  </Typography>
                  <Typography variant="h6">
                    {hqCasualties.total_hq_personnel > 0 
                      ? ((hqCasualties.total / hqCasualties.total_hq_personnel) * 100).toFixed(1)
                      : 0}% 
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' '}over {duration} days
                    </Typography>
                  </Typography>
                </Box>

                {/* Quick Breakdown */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Casualty Types:
                  </Typography>
                  {/* BUG-T33 fix: complementary rounding — compute combat%, DNBI = 100 - combat% */}
                  {(() => {
                    const combatCount = (hqCasualties.kia || 0) + (hqCasualties.dow || 0) + (hqCasualties.wia || 0);
                    const combatPct = calcPercent(combatCount, hqCasualties.total);
                    const dnbiPct = hqCasualties.total > 0 ? 100 - combatPct : 0;
                    return (
                      <Grid container spacing={1}>
                        <Grid size={6}>
                          <Typography variant="body2">
                            • Combat: {formatNumber(combatCount)} ({combatPct}%)
                          </Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="body2">
                            • DNBI: {formatNumber(hqCasualties.dnbi || hqCasualties.total_dnbi || 0)} ({dnbiPct}%)
                          </Typography>
                        </Grid>
                      </Grid>
                    );
                  })()}
                </Box>

                {/* Expand Button */}
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  onClick={() => setHqExpanded(!hqExpanded)}
                  endIcon={hqExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ mt: 1 }}
                >
                  {hqExpanded ? 'Hide' : 'View'} Detailed Breakdown
                </Button>
              </Paper>
            </Grid>
          )}

          {/* SUPPORT UNITS CARD */}
          {supportCasualties && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: 3, 
                  bgcolor: 'warning.50',
                  border: '1px solid',
                  borderColor: 'warning.main',
                  height: '100%'
                }}
              >
                <Typography 
                  variant="h6" 
                  color="warning.dark" 
                  gutterBottom 
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
                >
                  SUPPORT UNITS
                </Typography>
                
                {/* Personnel and Casualties */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Personnel
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {formatNumber(supportCasualties.personnel)}
                    </Typography>
                  </Grid>
                  <Grid size={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Casualties
                    </Typography>
                    <Typography variant="h5" color="error" sx={{ fontWeight: 600 }}>
                      {formatNumber(supportCasualties.total)}
                    </Typography>
                  </Grid>
                </Grid>

                {/* Casualty Rate */}
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Casualty Rate
                  </Typography>
                  <Typography variant="h6">
                    {supportCasualties.personnel > 0 
                      ? ((supportCasualties.total / supportCasualties.personnel) * 100).toFixed(1)
                      : 0}% 
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' '}over {duration} days
                    </Typography>
                  </Typography>
                </Box>

                {/* Quick Breakdown */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Casualty Types:
                  </Typography>
                  {/* BUG-T33/T32 fix: complementary rounding for support unit totals */}
                  {(() => {
                    const combatCount = (supportCasualties.kia || 0) + (supportCasualties.dow || 0) + (supportCasualties.wia || 0);
                    const combatPct = calcPercent(combatCount, supportCasualties.total);
                    const dnbiPct = supportCasualties.total > 0 ? 100 - combatPct : 0;
                    return (
                      <Grid container spacing={1}>
                        <Grid size={6}>
                          <Typography variant="body2">
                            • Combat: {formatNumber(combatCount)} ({combatPct}%)
                          </Typography>
                        </Grid>
                        <Grid size={6}>
                          <Typography variant="body2">
                            • DNBI: {formatNumber(supportCasualties.dnbi || 0)} ({dnbiPct}%)
                          </Typography>
                        </Grid>
                      </Grid>
                    );
                  })()}
                </Box>

                {/* Expand Button */}
                <Button
                  fullWidth
                  variant="outlined"
                  color="warning"
                  onClick={() => setSupportExpanded(!supportExpanded)}
                  endIcon={supportExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ mt: 1 }}
                >
                  {supportExpanded ? 'Hide' : 'View'} Detailed Breakdown
                </Button>
              </Paper>
            </Grid>
          )}
        </Grid>

        {/* ============================================ */}
        {/* HQ DETAILED BREAKDOWN (EXPANDABLE)          */}
        {/* ============================================ */}
        {hqCasualties && (
          <Collapse in={hqExpanded}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 3, 
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', mb: 2 }}>
                HQ CASUALTIES - DETAILED BREAKDOWN
              </Typography>

              {/* Casualty Types Table */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1 }}>
                CASUALTY TYPES (All {hqCasualties.total} casualties from HQ staff)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>%</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>KIA</TableCell>
                      <TableCell align="right">{hqCasualties.kia || 0}</TableCell>
                      <TableCell align="right">{calcPercent(hqCasualties.kia || 0, hqCasualties.total)}%</TableCell>
                      <TableCell>CP attack, indirect fire</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>DOW</TableCell>
                      <TableCell align="right">{hqCasualties.dow || 0}</TableCell>
                      <TableCell align="right">{calcPercent(hqCasualties.dow || 0, hqCasualties.total)}%</TableCell>
                      <TableCell>Died after evacuation</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>WIA</TableCell>
                      <TableCell align="right">{hqCasualties.wia || 0}</TableCell>
                      <TableCell align="right">{calcPercent(hqCasualties.wia || 0, hqCasualties.total)}%</TableCell>
                      <TableCell>Combat injuries</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'warning.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>DNBI</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {hqCasualties.dnbi || hqCasualties.total_dnbi || 0}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {calcPercent(hqCasualties.dnbi || hqCasualties.total_dnbi || 0, hqCasualties.total)}%
                      </TableCell>
                      <TableCell>Disease, accidents, illness</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 600 }}>TOTAL</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{hqCasualties.total}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>100%</TableCell>
                      <TableCell>-</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Where Casualties Occurred (By Echelon) */}
              {hqCasualties.personnel_breakdown && (
                <>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 1 }}>
                    WHERE CASUALTIES OCCURRED (By HQ Level)
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Echelon</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Casualties</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>% of Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {hqCasualties.personnel_breakdown.corps > 0 && (
                          <TableRow>
                            <TableCell>Corps HQ</TableCell>
                            <TableCell align="right">{hqCasualties.personnel_breakdown.corps}</TableCell>
                            <TableCell align="right">
                              {calcPercent(hqCasualties.personnel_breakdown.corps, hqCasualties.total)}%
                            </TableCell>
                          </TableRow>
                        )}
                        {hqCasualties.personnel_breakdown.division > 0 && (
                          <TableRow>
                            <TableCell>Division HQ</TableCell>
                            <TableCell align="right">{hqCasualties.personnel_breakdown.division}</TableCell>
                            <TableCell align="right">
                              {calcPercent(hqCasualties.personnel_breakdown.division, hqCasualties.total)}%
                            </TableCell>
                          </TableRow>
                        )}
                        {hqCasualties.personnel_breakdown.brigade > 0 && (
                          <TableRow>
                            <TableCell>Brigade HQs</TableCell>
                            <TableCell align="right">{hqCasualties.personnel_breakdown.brigade}</TableCell>
                            <TableCell align="right">
                              {calcPercent(hqCasualties.personnel_breakdown.brigade, hqCasualties.total)}%
                            </TableCell>
                          </TableRow>
                        )}
                        {hqCasualties.personnel_breakdown.battalion > 0 && (
                          <TableRow>
                            <TableCell>Battalion HQs</TableCell>
                            <TableCell align="right">{hqCasualties.personnel_breakdown.battalion}</TableCell>
                            <TableCell align="right">
                              {calcPercent(hqCasualties.personnel_breakdown.battalion, hqCasualties.total)}%
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* DNBI Categories Breakdown */}
              {hqCasualties.dnbi_categories && (hqCasualties.dnbi || hqCasualties.total_dnbi) > 0 && (
                <>
                  {/* BUG-5J-001: Compute category sum separately; use it as % denominator so
                      percentages sum to 100%. If categorySum < dnbiTotal (calculator rounding
                      residual), show an "Unclassified" row for the difference. */}
                  {(() => {
                    const dnbiTotal = hqCasualties.dnbi || hqCasualties.total_dnbi || 0;
                    const entries   = Object.entries(hqCasualties.dnbi_categories);
                    const catSum    = entries.reduce((s, [, v]) => s + v, 0);
                    const residual  = dnbiTotal - catSum;
                    const pctDenom  = catSum > 0 ? catSum : dnbiTotal;
                    return (
                      <>
                        <Typography variant="subtitle2" gutterBottom sx={{ mt: 3, mb: 1 }}>
                          DNBI BREAKDOWN ({dnbiTotal} of {hqCasualties.total} casualties are DNBI)
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ bgcolor: 'warning.100' }}>
                                <TableCell sx={{ fontWeight: 600 }}>DNBI Category</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>% of DNBI</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Treatment Location</TableCell>
                              </TableRow>
                            </TableHead>
                            {/* BUG-T34 fix: use largest-remainder to prevent 101% sum */}
                            {(() => {
                              const counts = entries.map(([, v]) => v);
                              const pcts = largestRemainderPcts(counts, pctDenom);
                              return (
                                <TableBody>
                                  {entries.map(([category, count], idx) => (
                                    <TableRow key={category}>
                                      <TableCell sx={{ textTransform: 'capitalize' }}>
                                        {category.replace(/_/g, ' ')}
                                      </TableCell>
                                      <TableCell align="right">{count}</TableCell>
                                      <TableCell align="right">
                                        {pcts[idx]}%
                                      </TableCell>
                                      <TableCell>
                                        {category === 'mental_health' ? 'Role 3/4 + Specialist' :
                                         category === 'musculoskeletal' ? 'Role 2/3' :
                                         category === 'infectious' ? 'Role 2/3' :
                                         'Varies'}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                  {residual > 0 && (
                                    <TableRow>
                                      <TableCell sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                        Unclassified (rounding)
                                      </TableCell>
                                      <TableCell align="right" sx={{ color: 'text.secondary' }}>{residual}</TableCell>
                                      <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                        {calcPercent(residual, dnbiTotal)}%
                                      </TableCell>
                                      <TableCell sx={{ color: 'text.secondary' }}>-</TableCell>
                                    </TableRow>
                                  )}
                                </TableBody>
                              );
                            })()}
                          </Table>
                        </TableContainer>
                      </>
                    );
                  })()}
                </>
              )}
            </Paper>
          </Collapse>
        )}

        {/* ============================================ */}
        {/* SUPPORT DETAILED BREAKDOWN (EXPANDABLE)     */}
        {/* ============================================ */}
        {supportCasualties && (
          <Collapse in={supportExpanded}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 3, 
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: 'warning.dark', mb: 2 }}>
                SUPPORT CASUALTIES - DETAILED BREAKDOWN
              </Typography>

              {/* Casualty Types Table */}
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2, mb: 1 }}>
                CASUALTY TYPES (All {supportCasualties.total} casualties from support units)
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Count</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>%</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>KIA</TableCell>
                      <TableCell align="right">{supportCasualties.kia || 0}</TableCell>
                      <TableCell align="right">{calcPercent(supportCasualties.kia || 0, supportCasualties.total)}%</TableCell>
                      <TableCell>Convoy attacks, rear strikes</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>DOW</TableCell>
                      <TableCell align="right">{supportCasualties.dow || 0}</TableCell>
                      <TableCell align="right">{calcPercent(supportCasualties.dow || 0, supportCasualties.total)}%</TableCell>
                      <TableCell>Died after evacuation</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>WIA</TableCell>
                      <TableCell align="right">{supportCasualties.wia || 0}</TableCell>
                      <TableCell align="right">{calcPercent(supportCasualties.wia || 0, supportCasualties.total)}%</TableCell>
                      <TableCell>Combat injuries</TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'warning.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>DNBI</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {supportCasualties.dnbi || 0}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {calcPercent(supportCasualties.dnbi || 0, supportCasualties.total)}%
                      </TableCell>
                      <TableCell>Disease, accidents, illness</TableCell>
                    </TableRow>
                    {/* BUG-T32 fix: recompute total from components to avoid off-by-one rounding */}
                    {(() => {
                      const compTotal = (supportCasualties.kia || 0) + (supportCasualties.dow || 0)
                                      + (supportCasualties.wia || 0) + (supportCasualties.dnbi || 0);
                      return (
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell sx={{ fontWeight: 600 }}>TOTAL</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>{compTotal}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>100%</TableCell>
                          <TableCell>-</TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Support Units Include:</strong> Signal, Medical, Logistics, Transportation,
                  Maintenance, Military Police, Engineer (division-level), and other rear-area support elements.
                </Typography>
              </Alert>
            </Paper>
          </Collapse>
        )}

        {/* ============================================ */}
        {/* MEDICAL PLANNING CONTEXT                    */}
        {/* ============================================ */}
        <Divider sx={{ my: 3 }} />
        
        <Paper 
          elevation={0}
          sx={{ 
            p: 3, 
            bgcolor: 'info.50',
            border: '1px solid',
            borderColor: 'info.light'
          }}
        >
          <Typography 
            variant="subtitle1" 
            gutterBottom 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 600,
              color: 'info.dark',
              mb: 2
            }}
          >
            <InfoIcon color="info" />
            {t('tab5.hq_support.medical_planning_title')}
          </Typography>

          <Box component="ul" sx={{ mt: 0, mb: 0, pl: 3 }}>
            <li>
              <Typography variant="body2" paragraph>
                <strong>{t('tab5.hq_support.lower_evac_title')}:</strong> {t('tab5.hq_support.lower_evac_body')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>{t('tab5.hq_support.rear_facilities_title')}:</strong> {t('tab5.hq_support.rear_facilities_body')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>{t('tab5.hq_support.dnbi_impact_title')}:</strong> {t('tab5.hq_support.dnbi_impact_pre')}
                {formatNumber((hqCasualties?.dnbi || hqCasualties?.total_dnbi || 0) + (supportCasualties?.dnbi || 0))}{t('tab5.hq_support.dnbi_impact_post')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2" paragraph>
                <strong>{t('tab5.hq_support.total_rear_title')}:</strong>{' '}
                {formatNumber((hqCasualties?.total || 0) + (supportCasualties?.total || 0))}{t('tab5.hq_support.total_rear_pre')}<strong>{t('tab5.hq_support.included')}</strong>{t('tab5.hq_support.total_rear_post')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                <strong>{t('tab5.hq_support.nato_doctrine_title')}:</strong> {t('tab5.hq_support.nato_doctrine_body')}
              </Typography>
            </li>
          </Box>
        </Paper>
      </CardContent>
    </Card>
  );
};

/**
 * PropTypes validation
 */
NonCombatPersonnelSection.propTypes = {
  hqCasualties: PropTypes.shape({
    total_hq_personnel: PropTypes.number,
    total: PropTypes.number,
    kia: PropTypes.number,
    dow: PropTypes.number,
    wia: PropTypes.number,
    dnbi: PropTypes.number,
    total_dnbi: PropTypes.number,
    dnbi_rate_applied: PropTypes.number,
    personnel_breakdown: PropTypes.shape({
      corps: PropTypes.number,
      division: PropTypes.number,
      brigade: PropTypes.number,
      battalion: PropTypes.number
    }),
    dnbi_categories: PropTypes.shape({
      musculoskeletal: PropTypes.number,
      mental_health: PropTypes.number,
      infectious: PropTypes.number,
      accidents: PropTypes.number,
      other: PropTypes.number
    })
  }),
  supportCasualties: PropTypes.shape({
    personnel: PropTypes.number,
    total: PropTypes.number,
    kia: PropTypes.number,
    dow: PropTypes.number,
    wia: PropTypes.number,
    dnbi: PropTypes.number
  }),
  duration: PropTypes.number,
  nationalStandard: PropTypes.string,
  echelon: PropTypes.string
};

export default NonCombatPersonnelSection;
